// src/components/adoption/AdoptionPostFeed.jsx
"use client";

import {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import useUserData from "../../hooks/useUserData";
import { getUserTypeColor } from "../../utils/userTypeUtils";
import { adoptionAPI } from "../../services/adoptionAPI";
import { handleApiError } from "../../services/api";
import {
  showSuccessToast,
  showErrorToast,
} from "../../utils/notificationUtils";
import { normalizePetInfo } from "../../utils/petDataUtils";
import { fetchCitiesFromAPI } from "../../utils/locationData";
import AdoptionPost from "./AdoptionPost";
import "./AdoptionPostFeed.css";

const AdoptionPostFeed = forwardRef(
  (
    {
      currentUser,
      refreshTrigger = 0,
      onEditPost,
      onViewRequests,
      onOfferForAdoption,
      highlightedPostId,
      onViewDetails,
    },
    ref
  ) => {
    const { t, i18n } = useTranslation();
    const { user, getUserType } = useAuth();
    const { userData: fullUserData } = useUserData();
    
    // Get user type color
    const userType = getUserType();
    const userTypeColor = getUserTypeColor(userType);
    
    // Helper function to convert hex to rgba
    const hexToRgba = (hex, alpha) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };
    
    // Calculate color variants for CSS variables
    const colorVariants = {
      main: userTypeColor,
      shadow15: hexToRgba(userTypeColor, 0.15),
      shadow25: hexToRgba(userTypeColor, 0.25),
      shadow35: hexToRgba(userTypeColor, 0.35),
      bg10: hexToRgba(userTypeColor, 0.1),
    };

    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMorePosts, setHasMorePosts] = useState(true);
    const [filter, setFilter] = useState("all"); // 'all' | 'mine'
    const [activeHighlightId, setActiveHighlightId] = useState(null);
    const [attributeFilters, setAttributeFilters] = useState({
      city: "any",
      ageGroup: "any",
      species: "any",
      gender: "any",
    });
    const [cityOptions, setCityOptions] = useState([]);
    const mountedRef = useRef(true);
    const isLoadingRef = useRef(false);

    const isRTL = i18n.dir() === "rtl";
    const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

    useEffect(() => {
      mountedRef.current = true;
      return () => {
        mountedRef.current = false;
      };
    }, []);

    // Load cities for the current user's country so the City filter
    // always shows cities from their profile country.
    useEffect(() => {
      const fetchUserCountryCities = async () => {
        try {
          // Prefer user object from auth, but fall back to stored user_data
          const storedUser =
            fullUserData ||
            user ||
            (() => {
              try {
                const raw = localStorage.getItem("user_data");
                return raw ? JSON.parse(raw) : null;
              } catch {
                return null;
              }
            })();

          if (!storedUser) {
            setCityOptions([]);
            return;
          }

          const countryId =
            storedUser.country_id ??
            storedUser.countryId ??
            storedUser.country ?? // many user payloads store numeric id here
            storedUser.country_data?.id ??
            storedUser.country?.id ??
            storedUser.profile_country_id ??
            null;

          const countryCode =
            storedUser.country_code ??
            storedUser.countryCode ??
            storedUser.country_iso_code ??
            storedUser.country_data?.code ??
            storedUser.country?.code ??
            storedUser.country?.isoCode ??
            null;

          // Debug: see what we are using to fetch cities


          let cities = [];

          // Try by numeric country id first (same as CompleteProfile)
          if (countryId) {
            const token = localStorage.getItem("access_token");
            const response = await fetch(
              `${API_BASE_URL}/api/v1/locations/cities/country/${countryId}`,
              {
                headers: {
                  Accept: "application/json",
                  ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
              }
            );

            if (response.ok) {
              const result = await response.json();
              if (result.status === "success" && Array.isArray(result.data)) {
                cities = result.data;
              } else if (Array.isArray(result.data)) {
                cities = result.data;
              }
            }
          }

          // Fallback: use helper that fetches by country code if id path gave nothing
          if ((!cities || cities.length === 0) && countryCode) {
            const apiCities = await fetchCitiesFromAPI(countryCode);
            if (Array.isArray(apiCities) && apiCities.length > 0) {
              cities = apiCities;
            }
          }

          if (!cities || cities.length === 0) {
            console.warn("[Adoption] No cities found for user country");
            setCityOptions([]);
            return;
          }

          const names = cities
            .map((city) =>
              isRTL
                ? city.name_ar || city.name_en || city.name
                : city.name_en || city.name_ar || city.name
            )
            .filter((name) => typeof name === "string" && name.trim().length > 0);

          const uniqueSorted = Array.from(new Set(names)).sort((a, b) =>
            a.localeCompare(b, undefined, { sensitivity: "base" })
          );


          setCityOptions(uniqueSorted);
        } catch (err) {
          console.error("Error fetching cities for adoption filter:", err);
          setCityOptions([]);
        }
      };

      fetchUserCountryCities();
    }, [user, API_BASE_URL, isRTL]);

    // Load adoption posts
    // Note: Backend automatically filters posts by the authenticated user's country
    // (same logic as social feed consolidated-feed endpoint)
    const loadAdoptionPosts = async (page = 1, append = false) => {
      if (isLoadingRef.current || !mountedRef.current) return;

      try {
        isLoadingRef.current = true;
        setError(null);

        if (!append && mountedRef.current) {
          setLoading(true);
        }

        const response = await adoptionAPI.getAdoptionPosts({
          page,
          per_page: 10
        });

        if (!mountedRef.current) return;

        let postsData = [];
        if (response.data) {
          if (response.data.status === 'success' && response.data.data) {
            if (response.data.data.data && Array.isArray(response.data.data.data)) {
              postsData = response.data.data.data;
            } else if (Array.isArray(response.data.data)) {
              postsData = response.data.data;
            }
          } else if (Array.isArray(response.data)) {
            postsData = response.data;
          }
        }

        // Transform posts to match expected structure
        const transformedPosts = postsData.map(post => {
          // Handle pet_info - could be single object, array, or nested in pets array
          let petInfo = post.pet_info;
          if (!petInfo && post.pets && Array.isArray(post.pets)) {
            petInfo = post.pets;
          } else if (!petInfo && post.pet) {
            petInfo = post.pet;
          }

          // Normalize pet_info using utility function
          const normalizedPetInfo = normalizePetInfo(petInfo);

          return {
            id: post.id,
            pet_id: post.pet_id || post.pet_ids,
            author: {
              id: post.author?.id || post.user_id || post.user?.id,
              name:
                post.author?.name ||
                (post.author?.first_name && post.author?.last_name
                  ? `${post.author.first_name} ${post.author.last_name}`.trim()
                  : post.author?.first_name || post.author?.name || "Unknown"),
              username: post.author?.username || post.author?.name || "@unknown",
              avatar: post.author?.avatar || post.author?.profile_image_url,
              connection_status:
                post.author?.connection_status || post.connection_status || "none",
              // New: carry over guardian/vet subtype so we can show \"Rescuer\" etc.
              sub_type: post.author?.sub_type || post.author?.subType,
              sub_type_display_name:
                post.author?.sub_type_display_name ||
                post.author?.subTypeDisplayName,
            },
            pet_info: normalizedPetInfo,
            about_pet: post.about_pet || post.about_pet_text || '',
            looking_for: post.looking_for || post.looking_for_text || '',
            status: post.status || 'offered',
            created_at: post.created_at || post.createdAt,
            adoption_requests_count: post.adoption_requests_count || 0,
            additional_images: post.additional_images || [],
            vaccinated: typeof post.vaccinated !== 'undefined' ? post.vaccinated : null,
            my_request_status: post.my_request_status || null, // Include user's request status
            has_shared: post.has_shared || false, // Include share status
          };
        });

        if (append) {
          setPosts(prev => [...prev, ...transformedPosts]);
        } else {
          setPosts(transformedPosts);
        }

        // Check if there are more pages
        const totalPages = response.data?.meta?.last_page ||
          response.data?.last_page ||
          response.data?.data?.last_page || 1;
        setHasMorePosts(page < totalPages);

        setCurrentPage(page);
      } catch (err) {
        if (mountedRef.current) {
          setError(handleApiError(err));
        }
      } finally {
        if (mountedRef.current) {
          isLoadingRef.current = false;
          setLoading(false);
        }
      }
    };

    // Initial load and refresh on trigger change
    useEffect(() => {
      loadAdoptionPosts(1, false);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshTrigger]);

    // Apply inline highlight when parent passes a highlighted post id
    useEffect(() => {
      if (!highlightedPostId) return;

      setActiveHighlightId(highlightedPostId);

      const timeout = setTimeout(() => {
        setActiveHighlightId(null);
      }, 1800);

      return () => clearTimeout(timeout);
    }, [highlightedPostId]);

    // Load more posts
    const loadMorePosts = () => {
      if (hasMorePosts && !loading && !isLoadingRef.current) {
        loadAdoptionPosts(currentPage + 1, true);
      }
    };

    // Infinite scroll
    useEffect(() => {
      if (!hasMorePosts || loading || isLoadingRef.current) {
        return;
      }

      const handleScroll = () => {
        const scrollPosition = window.innerHeight + window.scrollY;
        const documentHeight = document.documentElement.scrollHeight;

        if (documentHeight - scrollPosition < 200 && posts.length >= 10) {
          loadMorePosts();
        }
      };

      let ticking = false;
      const throttledHandleScroll = () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            handleScroll();
            ticking = false;
          });
          ticking = true;
        }
      };

      window.addEventListener('scroll', throttledHandleScroll, { passive: true });

      return () => {
        window.removeEventListener('scroll', throttledHandleScroll);
      };
    }, [hasMorePosts, loading, posts.length, currentPage]);

    const handleOfferToAdopt = async (postId) => {
      try {
        // Optimistically update the post to show "Pending" immediately
        setPosts(prevPosts =>
          prevPosts.map(post =>
            post.id === postId
              ? { ...post, my_request_status: 'pending' }
              : post
          )
        );

        await adoptionAPI.sendAdoptionRequest(postId);
        showSuccessToast(t('adoption.postFeed.offerSuccess') || 'Your offer to adopt has been sent successfully!');

        // Refresh to get the latest data from backend
        loadAdoptionPosts(1, false);
      } catch (err) {
        // Revert the optimistic update on error
        setPosts(prevPosts =>
          prevPosts.map(post =>
            post.id === postId
              ? { ...post, my_request_status: null }
              : post
          )
        );
        showErrorToast(handleApiError(err) || t('adoption.postFeed.offerError') || 'Failed to send adoption offer');
        throw err;
      }
    };

    const handleEditPost = (post) => {
      if (onEditPost) {
        onEditPost(post);
      }
    };

    const handleDeletePost = async (postId) => {
      try {
        await adoptionAPI.deleteAdoptionPost(postId);
        showSuccessToast(t('adoption.postFeed.deleteSuccess') || 'Adoption post deleted successfully');
        loadAdoptionPosts(1, false);
      } catch (err) {
        showErrorToast(handleApiError(err) || t('adoption.postFeed.deleteError') || 'Failed to delete adoption post');
      }
    };

    // Expose method to update request count when a request is rejected
    useImperativeHandle(ref, () => ({
      decrementRequestCount: () => {
        // Decrement adoption_requests_count for all posts (since requests shown are for user's posts)
        setPosts(prev => prev.map(post => ({
          ...post,
          adoption_requests_count: Math.max(0, (post.adoption_requests_count || 0) - 1)
        })));
      }
    }), []);

    // --- Helpers for pet & filter metadata ---
    const getPrimaryPetFromPost = (post) => {
      if (!post) return {};
      if (post.pet_info && Array.isArray(post.pet_info) && post.pet_info.length) {
        return post.pet_info[0] || {};
      }
      if (post.pet_info && !Array.isArray(post.pet_info)) {
        return post.pet_info || {};
      }
      if (Array.isArray(post.pets) && post.pets.length) {
        return post.pets[0] || {};
      }
      if (post.pet) {
        return post.pet || {};
      }
      return {};
    };

    const getPetCity = (pet, post) => {
      if (!pet && !post) return null;
      return (
        pet?.city_name ||
        pet?.city ||
        pet?.city_en ||
        post?.city_name ||
        post?.city ||
        null
      );
    };

    const getAgeGroupKey = (pet) => {
      const raw = pet?.birthdate || pet?.birth_date;
      if (!raw) return null;
      const birth = new Date(raw);
      if (Number.isNaN(birth.getTime())) return null;
      const today = new Date();
      let years = today.getFullYear() - birth.getFullYear();
      let months = today.getMonth() - birth.getMonth();

      if (months < 0 || (months === 0 && today.getDate() < birth.getDate())) {
        years -= 1;
        months += 12;
      }

      const totalMonths = years * 12 + months;

      if (totalMonths < 12) return 'baby';
      if (totalMonths < 36) return 'young';
      if (totalMonths < 84) return 'adult';
      return 'senior';
    };

    const AGE_GROUP_LABELS = {
      baby: t('adoption.filters.ageGroups.baby') || 'Baby (< 1 year)',
      young: t('adoption.filters.ageGroups.young') || 'Young (1-3 years)',
      adult: t('adoption.filters.ageGroups.adult') || 'Adult (3-7 years)',
      senior: t('adoption.filters.ageGroups.senior') || 'Senior (7+ years)',
    };

    // Apply client-side filter for "All posts" vs "My posts"
    const filteredPostsByOwner = posts.filter((post) => {
      const status = (post.status || '').toLowerCase();

      // In the global feed, hide adopted posts so only active offers show
      if (filter === 'all') {
        return status !== 'adopted';
      }

      // In "My posts", show all of the current user's posts, including adopted ones
      if (filter === 'mine' && currentUser?.id) {
        return post.author?.id === currentUser.id;
      }

      return true;
    });

    // Build dropdown options + counts based on the posts that are
    // currently eligible for "All posts / My posts" (filteredPostsByOwner),
    // so counts automatically ignore adopted posts in the global view.
    const attributeFilterOptions = useMemo(() => {
      const cityCounts = new Map();
      const speciesCounts = new Map();
      const genderCounts = new Map();
      const ageGroupCounts = new Map();

      const increment = (map, key) => {
        if (!key) return;
        const current = map.get(key) || 0;
        map.set(key, current + 1);
      };

      filteredPostsByOwner.forEach((post) => {
        const pet = getPrimaryPetFromPost(post);
        const city = getPetCity(pet, post);

        const rawSpecies =
          typeof pet?.species === 'string'
            ? pet.species
            : pet?.species?.name;
        const rawBreed =
          typeof pet?.breed === 'string'
            ? pet.breed
            : pet?.breed?.name;
        const rawGender =
          typeof pet?.gender === 'string' ? pet.gender : null;

        const ageKey = getAgeGroupKey(pet);

        const normalize = (val) =>
          typeof val === 'string' ? val.trim() : null;

        const cityKey = normalize(city);
        const speciesKey = normalize(rawSpecies);
        const breedKey = normalize(rawBreed);
        const genderKey = normalize(rawGender);

        if (cityKey) increment(cityCounts, cityKey);
        if (speciesKey) increment(speciesCounts, speciesKey);
        // We no longer expose a Breed dropdown, so no need to
        // track a separate breedCounts map for the UI. Kept for
        // potential future use; can be removed later if unused.
        if (genderKey) increment(genderCounts, genderKey);
        if (ageKey) increment(ageGroupCounts, ageKey);
      });

      const sortStrings = (arr) =>
        arr.sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: 'base' })
        );

      // City options: prefer API-provided cityOptions order, fall back to what we see in posts.
      const cityNamesSource =
        Array.isArray(cityOptions) && cityOptions.length > 0
          ? cityOptions
          : sortStrings(Array.from(cityCounts.keys()));

      const cityOptionsWithCounts = cityNamesSource.map((name) => ({
        value: name,
        label: name,
        count: cityCounts.get(name) || 0,
      }));

      const makeOptionsFromMap = (map) =>
        sortStrings(Array.from(map.keys())).map((key) => ({
          value: key,
          label: key,
          count: map.get(key) || 0,
        }));

      const speciesOptions = makeOptionsFromMap(speciesCounts);
      const genderOptions = makeOptionsFromMap(genderCounts);

      const ageGroupOptions = Object.entries(AGE_GROUP_LABELS).map(
        ([key, label]) => ({
          value: key,
          label,
          count: ageGroupCounts.get(key) || 0,
        })
      );

      return {
        cities: cityOptionsWithCounts,
        species: speciesOptions,
        genders: genderOptions,
        ageGroups: ageGroupOptions,
      };
    }, [filteredPostsByOwner, cityOptions, AGE_GROUP_LABELS]);

    const handleAttributeFilterChange = (key, value) => {
      setAttributeFilters((prev) => ({
        ...prev,
        [key]: value,
      }));
    };

    const handleClearAttributeFilters = () => {
      setAttributeFilters({
        city: 'any',
        ageGroup: 'any',
        species: 'any',
        gender: 'any',
      });
    };

    const hasActiveAttributeFilters = useMemo(() => {
      return Object.values(attributeFilters).some((val) => val !== 'any');
    }, [attributeFilters]);

    if (loading && posts.length === 0) {
      return (
        <div className="adoption-post-feed-loading">
          <div className="adoption-post-feed-spinner"></div>
          <p>{t('adoption.feed.loading') || 'Loading adoption posts...'}</p>
        </div>
      );
    }

    if (error && posts.length === 0) {
      return (
        <div className="adoption-post-feed-error">
          <p>{error}</p>
          <button onClick={() => loadAdoptionPosts(1, false)}>
            {t('adoption.feed.retry') || 'Retry'}
          </button>
        </div>
      );
    }

    // Apply attribute-based filters (city, age, species, gender)
    const fullyFilteredPosts = filteredPostsByOwner.filter((post) => {
      const pet = getPrimaryPetFromPost(post);
      const petCity = getPetCity(pet, post);
      const ageGroupKey = getAgeGroupKey(pet);

      const normalized = (val) =>
        typeof val === 'string' ? val.trim().toLowerCase() : '';

      const petSpecies = normalized(
        typeof pet?.species === 'string' ? pet.species : pet?.species?.name
      );
      const petBreed = normalized(
        typeof pet?.breed === 'string' ? pet.breed : pet?.breed?.name
      );
      const petGender = normalized(pet?.gender);

      if (attributeFilters.city !== 'any') {
        if (!petCity || petCity !== attributeFilters.city) {
          return false;
        }
      }

      if (attributeFilters.ageGroup !== 'any') {
        if (!ageGroupKey || ageGroupKey !== attributeFilters.ageGroup) {
          return false;
        }
      }

      if (attributeFilters.species !== 'any') {
        if (!petSpecies || petSpecies !== normalized(attributeFilters.species)) {
          return false;
        }
      }

      if (attributeFilters.gender !== 'any') {
        if (!petGender || petGender !== normalized(attributeFilters.gender)) {
          return false;
        }
      }

      return true;
    });

    const showBaseEmpty = !loading && posts.length === 0;
    const showFilteredEmpty = !loading && posts.length > 0 && fullyFilteredPosts.length === 0;

    return (
      <div 
        className="adoption-post-feed"
        style={{
          '--adoption-feed-user-type-color': colorVariants.main,
          '--adoption-feed-user-type-shadow-15': colorVariants.shadow15,
          '--adoption-feed-user-type-shadow-25': colorVariants.shadow25,
          '--adoption-feed-user-type-shadow-35': colorVariants.shadow35,
          '--adoption-feed-user-type-bg-10': colorVariants.bg10,
        }}
      >
        <div className="adoption-post-feed-toolbar">
          <div className="adoption-post-feed-filters">
            <button
              type="button"
              className={`adoption-feed-chip ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              {t('adoption.feed.filterAll') || 'All posts'}
            </button>
            <button
              type="button"
              className={`adoption-feed-chip ${filter === 'mine' ? 'active' : ''}`}
              onClick={() => setFilter('mine')}
              disabled={!currentUser?.id}
            >
              {t('adoption.feed.filterMine') || 'My posts'}
            </button>
          </div>
        </div>

        {!loading && (
          <div className={`adoption-post-feed-main ${isRTL ? 'adoption-post-feed-main-rtl' : ''}`}>
            {/* Filters sidebar */}
            <aside className="adoption-filters-sidebar">
              <div className="adoption-filters-header">
                <h3 className="adoption-filters-title">
                  {t('adoption.filters.title') || 'Filters'}
                </h3>
                {hasActiveAttributeFilters && (
                  <button
                    type="button"
                    className="adoption-filters-clear"
                    onClick={handleClearAttributeFilters}
                  >
                    {t('adoption.filters.clearAll') || 'Clear all'}
                  </button>
                )}
              </div>

              <div className="adoption-filter-group">
                <label className="adoption-filter-label">
                  {t('adoption.filters.city') || 'City'}
                </label>
                <select
                  className="adoption-filter-select"
                  value={attributeFilters.city}
                  onChange={(e) =>
                    handleAttributeFilterChange('city', e.target.value)
                  }
                >
                  <option value="any">
                    {t('adoption.filters.any') || 'Any'}
                  </option>
                  {attributeFilterOptions.cities.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                      {opt.count ? ` (${opt.count})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div className="adoption-filter-group">
                <label className="adoption-filter-label">
                  {t('adoption.filters.age') || 'Age'}
                </label>
                <select
                  className="adoption-filter-select"
                  value={attributeFilters.ageGroup}
                  onChange={(e) =>
                    handleAttributeFilterChange('ageGroup', e.target.value)
                  }
                >
                  <option value="any">
                    {t('adoption.filters.any') || 'Any'}
                  </option>
                  {attributeFilterOptions.ageGroups.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                      {opt.count ? ` (${opt.count})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div className="adoption-filter-group">
                <label className="adoption-filter-label">
                  {t('adoption.filters.species') || 'Species'}
                </label>
                <select
                  className="adoption-filter-select"
                  value={attributeFilters.species}
                  onChange={(e) =>
                    handleAttributeFilterChange('species', e.target.value)
                  }
                >
                  <option value="any">
                    {t('adoption.filters.any') || 'Any'}
                  </option>
                  {attributeFilterOptions.species.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                      {opt.count ? ` (${opt.count})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div className="adoption-filter-group">
                <label className="adoption-filter-label">
                  {t('adoption.filters.gender') || 'Gender'}
                </label>
                <select
                  className="adoption-filter-select"
                  value={attributeFilters.gender}
                  onChange={(e) =>
                    handleAttributeFilterChange('gender', e.target.value)
                  }
                >
                  <option value="any">
                    {t('adoption.filters.any') || 'Any'}
                  </option>
                  {attributeFilterOptions.genders.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                      {opt.count ? ` (${opt.count})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </aside>

            {/* Results column */}
            <section className="adoption-results-column">
              {showBaseEmpty ? (
                <div className="adoption-post-feed-empty adoption-post-feed-empty-inline">
                  <img
                    src="/icons/adoptionnoposts.png"
                    alt={t('adoption.feed.emptyImageAlt') || 'No adoption posts illustration'}
                    className="adoption-post-feed-empty-image"
                  />
                  <h3>{t('adoption.feed.emptyTitle') || 'No adoption posts'}</h3>
                  <p>{t('adoption.feed.empty') || 'No adoption posts available yet.'}</p>
                  {typeof onOfferForAdoption === 'function' && (
                    <button
                      type="button"
                      className="adoption-post-feed-empty-cta"
                      onClick={onOfferForAdoption}
                    >
                      {t('adoption.feed.emptyCta') || 'Offer a pet for adoption'}
                    </button>
                  )}
                </div>
              ) : showFilteredEmpty ? (
                <div className="adoption-post-feed-empty adoption-post-feed-empty-inline">
                  <img
                    src="/icons/adoptionnoposts.png"
                    alt={
                      t('adoption.feed.emptyImageAlt') ||
                      'No adoption posts illustration'
                    }
                    className="adoption-post-feed-empty-image"
                  />
                  <h3>
                    {t('adoption.feed.emptyFilteredTitle') ||
                      'No adoption posts'}
                  </h3>
                  <p>
                    {t('adoption.feed.emptyFiltered') ||
                      'No posts match your current filters.'}
                  </p>
                </div>
              ) : (
                <div className="adoption-post-grid">
                  {fullyFilteredPosts.map((post, index) => (
                    <AdoptionPost
                      key={post.id}
                      post={post}
                      currentUser={currentUser}
                      onOfferToAdopt={handleOfferToAdopt}
                      onEdit={handleEditPost}
                      onDelete={handleDeletePost}
                      onViewRequests={onViewRequests}
                      onViewDetails={onViewDetails}
                      isHighlighted={
                        !!activeHighlightId &&
                        (activeHighlightId === post.id ||
                          (activeHighlightId === 'newest' && index === 0))
                      }
                    />
                  ))}
                </div>
              )}
            </section>
          </div>
        )}

        {hasMorePosts && !showBaseEmpty && !showFilteredEmpty && (
          <div className="adoption-post-feed-load-more">
            <button
              onClick={loadMorePosts}
              disabled={loading || isLoadingRef.current}
              className="adoption-post-feed-load-more-btn"
            >
              {loading ? (
                <>
                  <div className="adoption-post-feed-spinner"></div>
                  {t('adoption.feed.loadingMore') || 'Loading...'}
                </>
              ) : (
                t('adoption.feed.loadMore') || 'Load More'
              )}
            </button>
          </div>
        )}
      </div>
    );
  });

AdoptionPostFeed.displayName = 'AdoptionPostFeed';

export default AdoptionPostFeed;

